import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    RebelColors,
    RebelCard,
    RebelSectionTitle,
    RebelIcon,
    RebelLoadingSpinner,
    RebelContainer,
    RebelModal,
    RebelModalHeader,
    RebelModalBody,
    RebelModalFooter,
    RebelSecondaryButton,
    RebelDangerButton
} from "../../../styles/rebel-experiences";
import { GetDB } from "../../../utils/database/firebase-budget-tool";
import { listenToCollection, deleteDocument } from "../../../utils/firestore-functions";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * Admin page that lists all rebel client budgets.
 * Shows contact name, email, company, dates, number of properties, and last updated.
 * Clicking a row opens the budget for viewing/editing.
 */
const RebelBudgetClients = ({ collapsed }) => {
    const [budgets, setBudgets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortField, setSortField] = useState("updatedAt");
    const [sortDir, setSortDir] = useState("desc");
    const [searchTerm, setSearchTerm] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null); // {id, contactName, companyName}
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [copiedViewId, setCopiedViewId] = useState(null);

    const budgetDB = GetDB();

    useEffect(() => {
        const unsubscribe = listenToCollection(budgetDB, "rebelClientBudgets", (docs) => {
            setBudgets(docs || []);
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    // Format Firestore timestamp or date string
    const formatDate = (val) => {
        if (!val) return "—";
        let date;
        if (val.toDate) {
            date = val.toDate();
        } else if (val.seconds) {
            date = new Date(val.seconds * 1000);
        } else {
            date = new Date(val);
        }
        if (isNaN(date.getTime())) return "—";
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatDateTime = (val) => {
        if (!val) return "—";
        let date;
        if (val.toDate) {
            date = val.toDate();
        } else if (val.seconds) {
            date = new Date(val.seconds * 1000);
        } else {
            date = new Date(val);
        }
        if (isNaN(date.getTime())) return "—";
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    // Sort budgets
    const sorted = [...budgets].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        // Handle Firestore timestamps
        if (aVal && aVal.seconds) aVal = aVal.seconds;
        if (bVal && bVal.seconds) bVal = bVal.seconds;
        if (aVal && aVal.toDate) aVal = aVal.toDate().getTime();
        if (bVal && bVal.toDate) bVal = bVal.toDate().getTime();

        // String comparison
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();

        if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
        return 0;
    });

    // Filter by search term (includes shared recipients)
    const filtered = sorted.filter(b => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        const sharedMatch = (b.sharedRecipients || []).some(r =>
            (r.name || "").toLowerCase().includes(term) ||
            (r.email || "").toLowerCase().includes(term)
        );
        return (
            (b.contactName || "").toLowerCase().includes(term) ||
            (b.email || "").toLowerCase().includes(term) ||
            (b.companyName || "").toLowerCase().includes(term) ||
            (b.id || "").toLowerCase().includes(term) ||
            sharedMatch
        );
    });

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDir("asc");
        }
    };

    const openBudget = (id) => {
        window.location = `/r/${id}`;
    };

    const copyUrl = (e, id) => {
        e.stopPropagation(); // Don't trigger row click
        const url = `${window.location.origin}/r/${id}`;
        navigator.clipboard.writeText(url);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const copyViewUrl = (e, id) => {
        e.stopPropagation(); // Don't trigger row click
        const url = `${window.location.origin}/r/v/${id}`;
        navigator.clipboard.writeText(url);
        setCopiedViewId(id);
        setTimeout(() => setCopiedViewId(null), 2000);
    };

    const confirmDelete = (e, budget) => {
        e.stopPropagation(); // Don't trigger row click
        setDeleteTarget({
            id: budget.id || budget._docId,
            contactName: budget.contactName || "Unknown",
            companyName: budget.companyName || ""
        });
        setShowDeleteModal(true);
    };

    const handleDelete = () => {
        if (deleteTarget) {
            deleteDocument(budgetDB, "rebelClientBudgets", deleteTarget.id);
        }
        setShowDeleteModal(false);
        setDeleteTarget(null);
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <FontAwesomeIcon icon={['fas', 'chevron-down']} style={{ opacity: 0.2, marginLeft: 4, fontSize: 10 }} />;
        return <FontAwesomeIcon icon={['fas', sortDir === 'asc' ? 'chevron-up' : 'chevron-down']} style={{ marginLeft: 4, fontSize: 10 }} />;
    };

    if (loading) {
        return (
            <RebelContainer>
                <WideContentWrapper>
                    <RebelCard>
                        <div style={{ textAlign: 'center', padding: '48px' }}>
                            <RebelLoadingSpinner />
                            <p style={{ marginTop: '16px', color: '#b0b0b0' }}>Loading leads...</p>
                        </div>
                    </RebelCard>
                </WideContentWrapper>
            </RebelContainer>
        );
    }

    return (
        <RebelContainer>
            <WideContentWrapper>
                {/* Delete confirmation modal */}
                <RebelModal isOpen={showDeleteModal} toggle={() => setShowDeleteModal(false)}>
                    <RebelModalHeader>
                        Delete Lead Budget?
                    </RebelModalHeader>
                    <RebelModalBody>
                        {deleteTarget && (
                            <DeleteWarning>
                                <p>Are you sure you want to delete this budget?</p>
                                <DeleteDetails>
                                    <DetailRow>
                                        <DetailLabel>Contact:</DetailLabel>
                                        <DetailValue>{deleteTarget.contactName}</DetailValue>
                                    </DetailRow>
                                    {deleteTarget.companyName && (
                                        <DetailRow>
                                            <DetailLabel>Company:</DetailLabel>
                                            <DetailValue>{deleteTarget.companyName}</DetailValue>
                                        </DetailRow>
                                    )}
                                    <DetailRow>
                                        <DetailLabel>ID:</DetailLabel>
                                        <DetailValue style={{ fontSize: '0.8rem', opacity: 0.7 }}>{deleteTarget.id}</DetailValue>
                                    </DetailRow>
                                </DeleteDetails>
                                <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: 12, marginBottom: 0 }}>
                                    This action cannot be undone. The client's saved link will stop working.
                                </p>
                            </DeleteWarning>
                        )}
                    </RebelModalBody>
                    <RebelModalFooter>
                        <RebelSecondaryButton onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </RebelSecondaryButton>
                        <RebelDangerButton onClick={handleDelete}>
                            <RebelIcon icon={['fas', 'trash']} />
                            Delete Budget
                        </RebelDangerButton>
                    </RebelModalFooter>
                </RebelModal>

                <RebelCard>
                    <HeaderRow>
                        <RebelSectionTitle>
                            <RebelIcon icon={['fas', 'users']} />
                            Leads
                        </RebelSectionTitle>
                        <HeaderRight>
                            <CountBadge>{filtered.length} budget{filtered.length !== 1 ? 's' : ''}</CountBadge>
                            <SearchInput
                                type="text"
                                placeholder="Search by name, email, or company..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </HeaderRight>
                    </HeaderRow>

                    {filtered.length === 0 ? (
                        <EmptyState>
                            <FontAwesomeIcon icon={['fas', 'calculator']} style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }} />
                            <p>{searchTerm ? 'No budgets match your search.' : 'No client budgets have been created yet.'}</p>
                        </EmptyState>
                    ) : (
                        <TableContainer>
                            <Table>
                                <thead>
                                    <tr>
                                        <TH onClick={() => handleSort("contactName")}>
                                            Contact <SortIcon field="contactName" />
                                        </TH>
                                        <TH onClick={() => handleSort("email")}>
                                            Email <SortIcon field="email" />
                                        </TH>
                                        <TH onClick={() => handleSort("companyName")}>
                                            Company <SortIcon field="companyName" />
                                        </TH>
                                        <TH onClick={() => handleSort("startDate")}>
                                            Dates <SortIcon field="startDate" />
                                        </TH>
                                        <TH style={{ textAlign: 'center' }}>
                                            Properties
                                        </TH>
                                        <TH style={{ textAlign: 'center' }}>
                                            Attendees
                                        </TH>
                                        <TH style={{ cursor: 'default' }}>
                                            Shared With
                                        </TH>
                                        <TH onClick={() => handleSort("updatedAt")}>
                                            Last Updated <SortIcon field="updatedAt" />
                                        </TH>
                                        <TH style={{ textAlign: 'center', cursor: 'default' }}>
                                            Actions
                                        </TH>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(budget => {
                                        const budgetDocId = budget.id || budget._docId;
                                        return (
                                            <TR key={budgetDocId} onClick={() => openBudget(budgetDocId)}>
                                                <TD>
                                                    <ContactName>{budget.contactName || "—"}</ContactName>
                                                </TD>
                                                <TD>
                                                    <EmailText>{budget.email || "—"}</EmailText>
                                                </TD>
                                                <TD>
                                                    <CompanyText>{budget.companyName || "—"}</CompanyText>
                                                </TD>
                                                <TD>
                                                    {budget.startDate && budget.endDate
                                                        ? `${budget.startDate} — ${budget.endDate}`
                                                        : "—"}
                                                </TD>
                                                <TD style={{ textAlign: 'center' }}>
                                                    <PropertyCount>{(budget.properties || []).length}</PropertyCount>
                                                </TD>
                                                <TD style={{ textAlign: 'center' }}>
                                                    {budget.attendees || "—"}
                                                </TD>
                                                <TD>
                                                    {(budget.sharedRecipients && budget.sharedRecipients.length > 0) ? (
                                                        <SharedList>
                                                            {budget.sharedRecipients.map((r, i) => (
                                                                <SharedRecipient key={`${r.email}-${i}`}>
                                                                    <SharedName>{r.name}</SharedName>
                                                                    <SharedEmail>{r.email}</SharedEmail>
                                                                </SharedRecipient>
                                                            ))}
                                                        </SharedList>
                                                    ) : (
                                                        <span style={{ color: RebelColors.textMuted, fontSize: '0.8rem' }}>—</span>
                                                    )}
                                                </TD>
                                                <TD>
                                                    <UpdatedText>{formatDateTime(budget.updatedAt)}</UpdatedText>
                                                </TD>
                                                <TD style={{ textAlign: 'center' }}>
                                                    <ActionButtons>
                                                        {(copiedId === budgetDocId || copiedViewId === budgetDocId) && (
                                                            <CopiedMessage>
                                                                {copiedViewId === budgetDocId ? 'View link copied!' : 'URL copied!'}
                                                            </CopiedMessage>
                                                        )}
                                                        <ActionBtn
                                                            title="Copy budget URL"
                                                            onClick={(e) => copyUrl(e, budgetDocId)}
                                                            $copied={copiedId === budgetDocId}
                                                        >
                                                            <FontAwesomeIcon icon={['fas', copiedId === budgetDocId ? 'check' : 'link']} />
                                                        </ActionBtn>
                                                        <ActionBtn
                                                            title="Copy view-only URL"
                                                            onClick={(e) => copyViewUrl(e, budgetDocId)}
                                                            $copied={copiedViewId === budgetDocId}
                                                            $view
                                                        >
                                                            <FontAwesomeIcon icon={['fas', copiedViewId === budgetDocId ? 'check' : 'eye']} />
                                                        </ActionBtn>
                                                        <ActionBtn
                                                            title="Delete budget"
                                                            $danger
                                                            onClick={(e) => confirmDelete(e, budget)}
                                                        >
                                                            <FontAwesomeIcon icon={['fas', 'trash']} />
                                                        </ActionBtn>
                                                    </ActionButtons>
                                                </TD>
                                            </TR>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </TableContainer>
                    )}
                </RebelCard>
            </WideContentWrapper>
        </RebelContainer>
    );
};

export default RebelBudgetClients;

// === Styled Components ===

const WideContentWrapper = styled.div`
    max-width: 1600px;
    margin: 0 auto;
    padding: 2rem;
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 12px;
`;

const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const CountBadge = styled.span`
    background: rgba(253, 181, 6, 0.15);
    border: 1px solid rgba(253, 181, 6, 0.3);
    color: ${RebelColors.gold};
    font-size: 0.8rem;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 20px;
    white-space: nowrap;
`;

const SearchInput = styled.input`
    background: ${RebelColors.gray700};
    border: 1px solid ${RebelColors.border};
    border-radius: 6px;
    color: ${RebelColors.text};
    padding: 8px 14px;
    font-size: 0.85rem;
    width: 280px;
    transition: border-color 0.2s ease;

    &::placeholder {
        color: ${RebelColors.textMuted};
    }

    &:focus {
        outline: none;
        border-color: ${RebelColors.gold};
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 48px 20px;
    color: ${RebelColors.textMuted};

    p {
        margin: 0;
        font-size: 0.95rem;
    }
`;

const TableContainer = styled.div`
    overflow-x: auto;
    border-radius: 8px;
    border: 1px solid ${RebelColors.border};
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
`;

const TH = styled.th`
    background: rgba(253, 181, 6, 0.08);
    color: ${RebelColors.gold};
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid rgba(253, 181, 6, 0.2);
    cursor: pointer;
    user-select: none;
    white-space: nowrap;

    &:hover {
        background: rgba(253, 181, 6, 0.14);
    }
`;

const TR = styled.tr`
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(253, 181, 6, 0.06);
    }

    &:not(:last-child) {
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }
`;

const TD = styled.td`
    padding: 14px 16px;
    color: ${RebelColors.text};
    vertical-align: middle;
`;

const ContactName = styled.span`
    font-weight: 600;
    color: ${RebelColors.text};
`;

const EmailText = styled.span`
    color: ${RebelColors.gold};
    font-size: 0.83rem;
`;

const CompanyText = styled.span`
    color: ${RebelColors.textMuted};
`;

const SharedList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const SharedRecipient = styled.div`
    display: flex;
    gap: 6px;
    align-items: baseline;
    font-size: 0.8rem;
    line-height: 1.3;
`;

const SharedName = styled.span`
    color: ${RebelColors.text};
    font-weight: 500;
    white-space: nowrap;
`;

const SharedEmail = styled.span`
    color: ${RebelColors.textMuted};
    font-size: 0.75rem;
`;

const PropertyCount = styled.span`
    background: rgba(253, 181, 6, 0.12);
    color: ${RebelColors.gold};
    font-weight: 600;
    font-size: 0.8rem;
    padding: 2px 10px;
    border-radius: 12px;
`;

const UpdatedText = styled.span`
    color: ${RebelColors.textMuted};
    font-size: 0.8rem;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
`;

const CopiedMessage = styled.span`
    color: #10b981;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
    animation: fadeIn 0.15s ease;

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const ActionBtn = styled.button`
    background: ${props => props.$copied
        ? 'rgba(16, 185, 129, 0.15)'
        : 'transparent'};
    border: 1px solid ${props => props.$copied
        ? 'rgba(16, 185, 129, 0.4)'
        : props.$danger
        ? 'rgba(239, 68, 68, 0.3)'
        : props.$view
        ? 'rgba(147, 197, 253, 0.3)'
        : 'rgba(253, 181, 6, 0.25)'};
    color: ${props => props.$copied
        ? '#10b981'
        : props.$danger
        ? '#ef4444'
        : props.$view
        ? '#93c5fd'
        : RebelColors.gold};
    border-radius: 6px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.75rem;
    transition: all 0.2s ease;

    &:hover {
        background: ${props => props.$danger
            ? 'rgba(239, 68, 68, 0.15)'
            : props.$view
            ? 'rgba(147, 197, 253, 0.12)'
            : 'rgba(253, 181, 6, 0.12)'};
        border-color: ${props => props.$danger
            ? '#ef4444'
            : props.$view
            ? '#93c5fd'
            : RebelColors.gold};
    }
`;

const DeleteWarning = styled.div`
    p {
        color: ${RebelColors.text};
        font-size: 0.95rem;
        margin: 0 0 12px 0;
    }
`;

const DeleteDetails = styled.div`
    background: rgba(239, 68, 68, 0.06);
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: 8px;
    padding: 12px 16px;
`;

const DetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 4px 0;

    &:not(:last-child) {
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }
`;

const DetailLabel = styled.span`
    color: ${RebelColors.textMuted};
    font-size: 0.85rem;
`;

const DetailValue = styled.span`
    color: ${RebelColors.text};
    font-size: 0.85rem;
    font-weight: 500;
`;
